import React, { useEffect, useRef, useState } from 'react';
import { useMutation } from '@apollo/client';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Typography from '@mui/material/Typography';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import type { ContractType } from '../../../types/ContractType.types';
import { DELETE_CONTRACT_TYPE_LOGO, SET_CONTRACT_TYPE_LOGO } from '../api/contractTypeLogo';
import {
  CONTRACT_TYPE_LOGO_ACCEPT,
  CONTRACT_TYPE_LOGO_MESSAGES,
  checkContractTypeLogoDisplayable,
  checkContractTypeLogoFile,
  contractTypeLogoErrorMessage,
  contractTypeLogoMimeTypeFor,
  formatContractTypeLogoSize,
} from '../services/contractTypeLogoFile';
import { useDialogUsage } from '../../usage-tracking/hooks/useDialogUsage';

// RITA-1526 — Fenster „Logo für <Vertragsart>“: Datei wählen (PNG/JPG/SVG bis 1 MB), Vorschau in
// Originalproportion, Speichern, Logo entfernen, Abbrechen. Die Vertragsart muss bereits gespeichert
// sein (die Tabelle öffnet den Dialog nur für Zeilen mit id). Meldungen des Backends werden
// unverändert angezeigt; SVG erscheint ausschließlich als <img>, nie als eingebettetes Markup.
// Nach der Vorprüfung fragt der Dialog den Browser, ob er die Datei wirklich anzeigen kann; bis
// zum Ergebnis bleibt Speichern gesperrt, eine nicht anzeigbare Datei wird mit Meldung verworfen
// (Codex-Review 28.09.2026). Das Backend prüft die Lesbarkeit unabhängig davon erneut.

export const CONTRACT_TYPE_LOGO_SAVED_MESSAGE = 'Logo gespeichert';
export const CONTRACT_TYPE_LOGO_REMOVED_MESSAGE = 'Logo entfernt';
export const CONTRACT_TYPE_LOGO_EMPTY_TEXT = 'Kein Logo hinterlegt';

interface Props {
  open: boolean;
  contractType: ContractType | null;
  onClose: () => void;
  /** Nach erfolgreichem Speichern oder Entfernen: Meldung für die Tabelle und der neue Stand. */
  onSaved: (message: string, contractType: ContractType) => void;
}

// Vorschau als Objekt-URL mit verlässlichem Typ: eine SVG-Datei ohne gemeldeten MIME-Typ
// würde der Browser im <img> sonst nicht zeichnen.
const createPreviewUrl = (file: File): string | null => {
  if (typeof URL === 'undefined' || typeof URL.createObjectURL !== 'function') return null;
  const type = contractTypeLogoMimeTypeFor(file);
  return URL.createObjectURL(type && type !== file.type ? new Blob([file], { type }) : file);
};

function ContractTypeLogoDialog({ open, contractType, onClose, onSaved }: Props) {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  // Anzeigeprüfung im Browser: läuft für die zuletzt gewählte Datei; ein Dateiwechsel, Abbrechen,
  // ein erneutes Öffnen oder das Verschwinden aus dem DOM macht ein noch ausstehendes Ergebnis
  // gegenstandslos (Zähler).
  const [checkingFile, setCheckingFile] = useState(false);
  const fileCheck = useRef(0);
  const [setLogo, { loading: saving }] = useMutation(SET_CONTRACT_TYPE_LOGO);
  const [deleteLogo, { loading: removing }] = useMutation(DELETE_CONTRACT_TYPE_LOGO);
  // Nutzungserfassung: genau ein Ereignis je Öffnung — Speichern/Entfernen bestätigt, Abbrechen nicht.
  const dialogUsage = useDialogUsage('einstellungen.vertragsarten.logo');
  const busy = saving || removing;
  // Die Tabelle setzt die Vertragsart beim Schließen sofort auf null; während der Ausblendung
  // bleibt hier die zuletzt gezeigte stehen, sonst flackern Titel und Vorschau („Logo für “).
  const [shown, setShown] = useState<ContractType | null>(contractType);
  useEffect(() => {
    if (contractType) setShown(contractType);
  }, [contractType]);

  // Objekt-URL der Vorschau wieder freigeben, sobald sie ersetzt wird oder der Dialog verschwindet.
  useEffect(
    () => () => {
      if (previewUrl && typeof URL.revokeObjectURL === 'function') URL.revokeObjectURL(previewUrl);
    },
    [previewUrl],
  );

  useEffect(() => {
    if (open) {
      fileCheck.current += 1;
      setCheckingFile(false);
      setSelectedFile(null);
      setPreviewUrl(null);
      setFileError(null);
      setSubmitError(null);
      dialogUsage.reset();
    }
  }, [open, contractType, dialogUsage]);

  // Beim Verschwinden des Dialogs darf ein spätes Prüfergebnis keinen Zustand mehr setzen.
  useEffect(
    () => () => {
      fileCheck.current += 1;
    },
    [],
  );

  const rejectFile = (message: string) => {
    fileCheck.current += 1;
    setCheckingFile(false);
    setFileError(message);
    setSelectedFile(null);
    setPreviewUrl(null);
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files && event.target.files[0];
    // Zurücksetzen, damit dieselbe Datei nach einer Ablehnung erneut gewählt werden kann.
    // eslint-disable-next-line no-param-reassign
    event.target.value = '';
    if (!file) return;
    setSubmitError(null);
    const check = checkContractTypeLogoFile(file);
    if (!check.ok) {
      rejectFile(check.message);
      return;
    }
    setFileError(null);
    setSelectedFile(file);
    const url = createPreviewUrl(file);
    setPreviewUrl(url);
    fileCheck.current += 1;
    if (!url) {
      setCheckingFile(false);
      return;
    }
    const token = fileCheck.current;
    setCheckingFile(true);
    checkContractTypeLogoDisplayable(url).then((displayable) => {
      if (token !== fileCheck.current) return;
      setCheckingFile(false);
      if (!displayable) rejectFile(CONTRACT_TYPE_LOGO_MESSAGES.unreadable);
    });
  };

  const handleCancel = () => {
    fileCheck.current += 1;
    setCheckingFile(false);
    dialogUsage.cancelled();
    onClose();
  };

  const handleSave = async () => {
    if (!selectedFile || !contractType) return;
    dialogUsage.confirmed();
    setSubmitError(null);
    try {
      const { data } = await setLogo({ variables: { id: contractType.id, logo: selectedFile } });
      const saved = data?.setContractTypeLogo as ContractType | undefined;
      if (!saved) {
        setSubmitError(CONTRACT_TYPE_LOGO_MESSAGES.saveFailed);
        return;
      }
      onSaved(CONTRACT_TYPE_LOGO_SAVED_MESSAGE, saved);
    } catch (error) {
      setSubmitError(contractTypeLogoErrorMessage(error, CONTRACT_TYPE_LOGO_MESSAGES.saveFailed));
    }
  };

  const handleRemove = async () => {
    if (!contractType) return;
    dialogUsage.confirmed();
    setSubmitError(null);
    try {
      const { data } = await deleteLogo({ variables: { id: contractType.id } });
      const removed = data?.deleteContractTypeLogo as ContractType | undefined;
      if (!removed) {
        setSubmitError(CONTRACT_TYPE_LOGO_MESSAGES.removeFailed);
        return;
      }
      onSaved(CONTRACT_TYPE_LOGO_REMOVED_MESSAGE, removed);
    } catch (error) {
      setSubmitError(contractTypeLogoErrorMessage(error, CONTRACT_TYPE_LOGO_MESSAGES.removeFailed));
    }
  };

  const shownType = contractType ?? shown;
  const title = shownType?.title ?? '';
  const currentLogo = shownType?.logo_data_url ?? null;
  let shownImage: { src: string; alt: string } | null = null;
  if (previewUrl) shownImage = { src: previewUrl, alt: 'Vorschau des neuen Logos' };
  else if (currentLogo) shownImage = { src: currentLogo, alt: `Aktuelles Logo ${title}` };

  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : handleCancel}
      maxWidth="xs"
      fullWidth
      aria-labelledby="contract-type-logo-dialog-title"
    >
      <DialogTitle id="contract-type-logo-dialog-title" sx={{ fontWeight: 800 }}>
        {`Logo für ${title}`}
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary">
          PNG, JPG oder SVG, höchstens 1 MB. Das Logo wird in seinen Originalproportionen angezeigt.
        </Typography>
        <Box
          data-testid="contract-type-logo-preview"
          sx={{
            mt: 2,
            minHeight: 140,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: '1px dashed',
            borderColor: 'divider',
            borderRadius: 1,
            p: 2,
          }}
        >
          {shownImage ? (
            <Box
              component="img"
              src={shownImage.src}
              alt={shownImage.alt}
              // Lädt die Vorschau der neuen Datei nicht, ist sie kein anzeigbares Bild.
              onError={previewUrl ? () => rejectFile(CONTRACT_TYPE_LOGO_MESSAGES.unreadable) : undefined}
              sx={{ maxWidth: '100%', maxHeight: 160, objectFit: 'contain' }}
            />
          ) : (
            <Typography color="text.secondary">{CONTRACT_TYPE_LOGO_EMPTY_TEXT}</Typography>
          )}
        </Box>
        {selectedFile && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            {`Neue Datei: ${selectedFile.name} (${formatContractTypeLogoSize(selectedFile.size)})`}
          </Typography>
        )}
        <Button
          component="label"
          variant="outlined"
          startIcon={<UploadFileIcon />}
          disabled={busy}
          sx={{ mt: 2, textTransform: 'none' }}
        >
          Datei wählen
          <input
            hidden
            type="file"
            accept={CONTRACT_TYPE_LOGO_ACCEPT}
            onChange={handleFileChange}
            data-testid="contract-type-logo-input"
            aria-label="Logodatei wählen"
          />
        </Button>
        {fileError && (
          <Alert severity="error" sx={{ mt: 2 }} data-testid="contract-type-logo-file-error">
            {fileError}
          </Alert>
        )}
        {submitError && (
          <Alert severity="error" sx={{ mt: 2 }} data-testid="contract-type-logo-submit-error">
            {submitError}
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleCancel} disabled={busy} sx={{ textTransform: 'none' }}>
          Abbrechen
        </Button>
        {currentLogo && (
          <Button
            color="error"
            onClick={handleRemove}
            disabled={busy}
            startIcon={removing ? <CircularProgress size={16} color="inherit" /> : undefined}
            sx={{ textTransform: 'none' }}
          >
            Logo entfernen
          </Button>
        )}
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={busy || !selectedFile || checkingFile}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
          sx={{ textTransform: 'none', fontWeight: 700 }}
        >
          Speichern
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default ContractTypeLogoDialog;
